// Excel number-format patterns for the dates in an .xlsx export, derived from
// the browser locale so the spreadsheet opens with the same day/month order the
// user sees in the app. The export service accepts only Excel date tokens and
// plain separators in these patterns, so anything the locale produces outside
// that set (literal words, quoted text, RTL marks) falls back to ISO order
// rather than being rejected server-side.

export interface ExcelDateFormats {
  dateFormat: string;
  datetimeFormat: string;
}

export const DEFAULT_EXCEL_DATE_FORMAT = "yyyy-mm-dd";
export const DEFAULT_EXCEL_DATETIME_FORMAT = "yyyy-mm-dd hh:mm";

// Mirrors the export service's pattern validation: Excel date/time tokens plus
// the separators Excel treats as literals without quoting.
const ALLOWED_PATTERN = /^[dmyhsAMP/:.,\- ]{1,32}$/;

const SAMPLE_DATE = new Date(Date.UTC(2024, 11, 31, 15, 45, 0));

const buildDatePattern = (locale: string): string | undefined => {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: "UTC"
    }).formatToParts(SAMPLE_DATE);
  } catch {
    return undefined;
  }
  let pattern = "";
  for (const part of parts) {
    switch (part.type) {
      case "year":
        pattern += "yyyy";
        break;
      case "month":
        pattern += "mm";
        break;
      case "day":
        pattern += "dd";
        break;
      case "literal":
        pattern += part.value;
        break;
      default:
        // An era or other locale decoration has no Excel token; use ISO order.
        return undefined;
    }
  }
  return pattern;
};

const localeUsesTwelveHourClock = (locale: string): boolean => {
  try {
    const { hour12, hourCycle } = new Intl.DateTimeFormat(locale, {
      hour: "numeric"
    }).resolvedOptions() as Intl.ResolvedDateTimeFormatOptions & {
      hourCycle?: string;
    };
    if (typeof hour12 === "boolean") return hour12;
    return hourCycle === "h11" || hourCycle === "h12";
  } catch {
    return false;
  }
};

export const getExcelDateFormats = (locale: string): ExcelDateFormats => {
  const datePattern = buildDatePattern(locale);
  if (!datePattern || !ALLOWED_PATTERN.test(datePattern)) {
    return {
      dateFormat: DEFAULT_EXCEL_DATE_FORMAT,
      datetimeFormat: DEFAULT_EXCEL_DATETIME_FORMAT
    };
  }
  const timePattern = localeUsesTwelveHourClock(locale) ? "h:mm AM/PM" : "hh:mm";
  const datetimePattern = `${datePattern} ${timePattern}`;
  return {
    dateFormat: datePattern,
    datetimeFormat: ALLOWED_PATTERN.test(datetimePattern)
      ? datetimePattern
      : DEFAULT_EXCEL_DATETIME_FORMAT
  };
};

// The IANA zone the export service renders datetimes in, so Excel shows the
// user's local wall-clock time rather than UTC.
export const getBrowserTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};
